import { useCallback, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  AnchorCommentTextarea,
  hasIncompleteMention,
  validateAnchorCommentText
} from '@jarvis/platform-file-review';
import { FloatingCommentDialog } from '../fileReview/FloatingCommentDialog';
import '../pdfViewer/JarvisPdfViewer.css';

export interface SpreadsheetCommentPopoverProps {
  mountId?: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
  rangeLabel: string;
  onCancel: () => void;
  onSave: (comment: string) => void | Promise<void>;
  onValidationError?: (message: string) => void;
}

/** Same floating comment UI as PDF / Word (movable + resizable). */
export function SpreadsheetCommentPopover({
  mountId,
  path,
  rangeLabel,
  onCancel,
  onSave,
  onValidationError
}: SpreadsheetCommentPopoverProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      document
        .querySelector<HTMLTextAreaElement>('.floating-comment-dialog textarea')
        ?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const handleSave = useCallback(async () => {
    const text = draft.trim();
    if (!text || saving) return;
    const validationError = validateAnchorCommentText(text);
    if (validationError) {
      onValidationError?.(validationError);
      return;
    }
    setSaving(true);
    try {
      await onSave(text);
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, onValidationError, saving]);

  const saveDisabled = saving || !draft.trim() || hasIncompleteMention(draft);

  return (
    <FloatingCommentDialog ariaLabel="Comment on selection" dialogClassName="jarvis-pdf-selection-comment">
      <p className="spreadsheet-comment-popover__range">{rangeLabel}</p>
      <AnchorCommentTextarea
        mountId={mountId}
        path={path}
        className="jarvis-pdf-highlight-popover__input"
        rows={9}
        placeholder="Comment on selection… Type @ to pick someone to notify"
        value={draft}
        disabled={saving}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void handleSave();
          }
        }}
      />
      <div className="jarvis-pdf-highlight-popover__actions">
        <button
          type="button"
          className="jarvis-pdf-highlight-popover__cancel"
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="jarvis-pdf-highlight-popover__save"
          disabled={saveDisabled}
          onClick={() => void handleSave()}
        >
          Save
        </button>
      </div>
    </FloatingCommentDialog>
  );
}
